// 行为埋点：攒批后上报到 /ai/analytics/events（后端 AnalyticsController），统计页见 qy-lg/docs/analytics-dashboard.html。
// 尽力而为：所有异常都吞掉，发送失败直接丢弃、不重试，绝不能影响页面功能。
// 不记录查询串、输入内容、手机号；设备标识复用 guest.js 的 X-Guest-Id，会话标识每个标签页一个（sessionStorage）。
import { baseURL } from '../axios/axios';
import { getGuestId } from '../Service/guest';

const EVENTS_PATH = 'ai/analytics/events';
const SESSION_KEY = 'lgfb.analytics.sid';
const FLUSH_DELAY_MS = 4000;
const MAX_BATCH = 20;
const MAX_QUEUE = 200;
const MAX_PROPS_CHARS = 2048;
const EVENT_NAME = /^[a-z][a-z0-9_]{1,47}$/;
// 这些 key 可能是个人信息或论文内容，前端就不发（后端还会再过滤一遍）
const SENSITIVE_KEYS = new Set([
    'phone', 'mobile', 'tel', 'password', 'pwd', 'token', 'accesstoken', 'code', 'smscode', 'idcard', 'email',
    'username', 'name', 'title', 'content', 'query', 'text', 'abstract',
]);

/** 支付成功由支付代码派发：window.dispatchEvent(new CustomEvent('lgfb:order-paid', { detail: { payNo, orderNo, kind } })) */
export const ORDER_PAID_EVENT = 'lgfb:order-paid';

let queue = [];
let flushTimer = null;
let memorySession = '';
let initialized = false;

const hasWindow = () => typeof window !== 'undefined' && typeof document !== 'undefined';

export const endpoint = () => {
    const base = typeof baseURL === 'string' && baseURL ? baseURL : '/';
    return `${base.replace(/\/+$/, '')}/${EVENTS_PATH}`;
};

const randomId = () => {
    try {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, '');
    } catch {
        // 旧浏览器
    }
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

/** 每个标签页一个会话标识，刷新不变，关掉标签页就没了 */
export const getSessionId = () => {
    if (memorySession) return memorySession;
    try {
        const stored = window.sessionStorage.getItem(SESSION_KEY) || '';
        memorySession = /^[A-Za-z0-9_-]{4,64}$/.test(stored) ? stored : randomId();
        window.sessionStorage.setItem(SESSION_KEY, memorySession);
    } catch {
        memorySession = memorySession || randomId();
    }
    return memorySession;
};

/** 只留简单值，去掉空值，字符串截断；整体超过 2KB 时从后往前丢 */
export const cleanProps = (props) => {
    if (!props || typeof props !== 'object') return undefined;
    const out = {};
    Object.keys(props).forEach((key) => {
        const value = props[key];
        if (value === undefined || value === null || value === '' || SENSITIVE_KEYS.has(key.toLowerCase())) return;
        if (typeof value === 'string') out[key] = value.length > 200 ? value.slice(0, 200) : value;
        else if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
        else if (typeof value === 'boolean') out[key] = value;
    });
    const keys = Object.keys(out);
    while (keys.length && JSON.stringify(out).length > MAX_PROPS_CHARS) delete out[keys.pop()];
    return Object.keys(out).length ? out : undefined;
};

const send = (events, { beacon = false } = {}) => {
    const body = JSON.stringify({ guestId: getGuestId(), sentAt: Date.now(), events });
    const url = endpoint();
    const sendBeacon = () => {
        try {
            if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
                return navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
            }
        } catch {
            // 静默
        }
        return false;
    };
    // 页面关闭 / 切走时用 sendBeacon（浏览器保证发出）；平时用 fetch keepalive。
    // text/plain 避免跨域预检；失败不重发，否则刷新时 promise reject 但请求其实已送达，会重复。
    if (beacon && sendBeacon()) return;
    try {
        if (typeof fetch !== 'function') throw new Error('no fetch');
        fetch(url, {
            method: 'POST',
            body,
            keepalive: true,
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        }).catch(() => {});
    } catch {
        // fetch 不可用或 keepalive 超额时同步抛错，这时请求没发出去，才退回 sendBeacon
        if (!beacon) sendBeacon();
    }
};

/** 立即把队列里的事件发出去；beacon = true 表示页面正在离开 */
export const flush = (options = {}) => {
    try {
        if (flushTimer) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }
        while (queue.length) send(queue.splice(0, MAX_BATCH), options);
    } catch {
        queue = [];
    }
};

const scheduleFlush = () => {
    if (queue.length >= MAX_BATCH) {
        flush();
        return;
    }
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
        flushTimer = null;
        flush();
    }, FLUSH_DELAY_MS);
};

/**
 * 记一条事件
 * @param {string} event 事件名（小写字母 / 数字 / 下划线）
 * @param {object} [props] 只放简单值；label 是统计页按维度拆分用的主维度，不要放用户输入
 * @param {{page?: string, ref?: string}} [options] page 缺省为当前 pathname；ref 只在首屏 page_view 上传
 */
export const track = (event, props, options = {}) => {
    try {
        if (!hasWindow() || typeof event !== 'string' || !EVENT_NAME.test(event)) return;
        const item = {
            event,
            ts: Date.now(),
            sid: getSessionId(),
            page: options.page || window.location.pathname,
        };
        if (options.ref) item.ref = String(options.ref).slice(0, 500);
        const cleaned = cleanProps(props);
        if (cleaned) item.props = cleaned;
        queue.push(item);
        if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
        scheduleFlush();
    } catch {
        // 静默
    }
};

const onTrackedClick = (e) => {
    try {
        const target = e.target && typeof e.target.closest === 'function' ? e.target.closest('[data-track]') : null;
        if (!target) return;
        const props = {};
        const label = target.getAttribute('data-track-label');
        const value = target.getAttribute('data-track-value');
        if (label) props.label = label;
        if (value) props.value = value;
        track(target.getAttribute('data-track'), props);
    } catch {
        // 静默
    }
};

const onOrderPaid = (e) => {
    try {
        const detail = (e && e.detail) || {};
        track('pay_success', {
            label: detail.kind,
            kind: detail.kind,
            payNo: detail.payNo,
            orderNo: detail.orderNo,
        });
        flush();
    } catch {
        // 静默
    }
};

// 在 document 上派发且不冒泡的也能收到；冒泡的交给 window 上那个，避免记两次
const onOrderPaidNoBubble = (e) => {
    if (!e.bubbles) onOrderPaid(e);
};
const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') flush({ beacon: true });
};
const onPageHide = () => flush({ beacon: true });

/**
 * 装一次全局监听：带 data-track="事件名" 的元素被点击时记事件（可配 data-track-label / data-track-value），
 * 支付代码派发的 lgfb:order-paid 记 pay_success，页面隐藏 / 关闭时立即上报。
 */
export const initAnalytics = () => {
    if (initialized || !hasWindow()) return;
    initialized = true;
    try {
        // 捕获阶段：点击处理里 stopPropagation 或马上跳转也能记到
        document.addEventListener('click', onTrackedClick, true);
        window.addEventListener(ORDER_PAID_EVENT, onOrderPaid);
        document.addEventListener(ORDER_PAID_EVENT, onOrderPaidNoBubble);
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', onPageHide);
    } catch {
        // 静默
    }
};

/** 只给单测用：清空队列、拆掉监听 */
export const __resetForTests = () => {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = null;
    queue = [];
    memorySession = '';
    if (initialized && hasWindow()) {
        document.removeEventListener('click', onTrackedClick, true);
        window.removeEventListener(ORDER_PAID_EVENT, onOrderPaid);
        document.removeEventListener(ORDER_PAID_EVENT, onOrderPaidNoBubble);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', onPageHide);
    }
    initialized = false;
};

export const __queueSize = () => queue.length;
